import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import ResponsiveImg from "../ResponsiveImg";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORY_TILE_SIZE = "8rem";
const CATEGORY_TILE_WIDTH = "9rem";

/**
 * CategoryCarousel — quick-jump circular tiles shown at the top of the
 * storefront home page on BOTH mobile and desktop.
 *
 * • 2 rows × horizontal scroll (Nykaa/Myntra-inspired, but boutique-themed).
 * • Mobile: native touch/swipe.
 * • Desktop: fades arrows in on hover. Arrow click scrolls one viewport
 *   width; arrows auto-hide at edges.
 * • Empty-state safe — backend returns top-N categories as fallback.
 */
export default function CategoryCarousel() {
    const [tiles, setTiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const scrollerRef = useRef(null);
    const [canL, setCanL] = useState(false);
    const [canR, setCanR] = useState(false);

    useEffect(() => {
        let alive = true;
        fetch(`${API}/storefront/mobile-carousel`)
            .then(r => (r.ok ? r.json() : []))
            .then(data => { if (alive) setTiles(Array.isArray(data) ? data : []); })
            .catch(() => { /* swallow */ })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, []);

    const recalcEdges = useCallback(() => {
        const el = scrollerRef.current;
        if (!el) return;
        const max = el.scrollWidth - el.clientWidth;
        setCanL(el.scrollLeft > 4);
        setCanR(el.scrollLeft < max - 4);
    }, []);

    useEffect(() => {
        const el = scrollerRef.current;
        if (!el) return;
        recalcEdges();
        const onScroll = () => recalcEdges();
        el.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", recalcEdges);
        return () => {
            el.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", recalcEdges);
        };
    }, [tiles, recalcEdges]);

    const scrollBy = (dir) => {
        const el = scrollerRef.current;
        if (!el) return;
        // Scroll by ~80% of visible width so the last visible column stays
        // partly visible after the page-turn (clearer visual continuity than
        // a hard 100% page-snap).
        el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" });
    };

    if (loading && tiles.length === 0) {
        return (
            <section
                className="py-3 px-3"
                aria-label="Loading boutique categories"
                data-testid="category-carousel-skeleton"
            >
                <div className="flex gap-3 sm:gap-5 overflow-x-auto no-scrollbar">
                    {Array.from({ length: 8 }).map((_, i) => (
                        <div key={i} className="flex-shrink-0 flex flex-col items-center gap-1.5" style={{ width: CATEGORY_TILE_WIDTH }}>
                            <div className="rounded-full bg-stone-200/70 animate-pulse" style={{ width: CATEGORY_TILE_SIZE, height: CATEGORY_TILE_SIZE }} />
                            <div className="w-14 h-2.5 rounded-full bg-stone-200/70 animate-pulse" />
                        </div>
                    ))}
                </div>
            </section>
        );
    }

    if (tiles.length === 0) return null;

    return (
        <section
            className="py-3 lg:py-4 group/carousel"
            aria-label="Shop by category"
            data-testid="category-carousel"
        >
            <div className="max-w-7xl mx-auto relative">
                {/* Carousel arrow buttons — overlaid on the edges (don't
                    reserve scroller padding) so the first tile sits flush
                    with the page gutter at rest. */}
                <button
                    type="button"
                    onClick={() => scrollBy(-1)}
                    aria-label="Scroll previous"
                    data-testid="carousel-prev"
                    disabled={!canL}
                    className={`absolute left-0 sm:left-1 top-1/2 -translate-y-1/2 z-10 w-9 h-9 lg:w-11 lg:h-11 flex items-center justify-center rounded-full bg-white/95 backdrop-blur shadow-lg border border-stone-200 text-ink hover:bg-rosegold hover:text-ivory hover:border-rosegold transition-all disabled:!opacity-0 disabled:pointer-events-none`}
                >
                    <ChevronLeft size={16} className="lg:hidden" />
                    <ChevronLeft size={18} className="hidden lg:block" />
                </button>
                <button
                    type="button"
                    onClick={() => scrollBy(1)}
                    aria-label="Scroll next"
                    data-testid="carousel-next"
                    disabled={!canR}
                    className={`absolute right-0 sm:right-1 top-1/2 -translate-y-1/2 z-10 w-9 h-9 lg:w-11 lg:h-11 flex items-center justify-center rounded-full bg-white/95 backdrop-blur shadow-lg border border-stone-200 text-ink hover:bg-rosegold hover:text-ivory hover:border-rosegold transition-all disabled:!opacity-0 disabled:pointer-events-none`}
                >
                    <ChevronRight size={16} className="lg:hidden" />
                    <ChevronRight size={18} className="hidden lg:block" />
                </button>

                {/* Edge-fade gradients give the cut tile on the scrollable
                    side a soft falloff so it feels intentional, not chopped. */}
                <div className={`pointer-events-none absolute left-0 top-0 bottom-0 w-10 lg:w-14 bg-gradient-to-r from-ivory/95 via-ivory/60 to-transparent z-[1] transition-opacity ${canL ? "opacity-100" : "opacity-0"}`} />
                <div className={`pointer-events-none absolute right-0 top-0 bottom-0 w-10 lg:w-14 bg-gradient-to-l from-ivory/95 via-ivory/60 to-transparent z-[1] transition-opacity ${canR ? "opacity-100" : "opacity-0"}`} />

                {/* Symmetric horizontal padding so the first tile sits the
                    same distance from the left edge as the last visible tile
                    does from the right — feels balanced at rest. */}
                <div
                    ref={scrollerRef}
                    className="flex gap-3 sm:gap-5 overflow-x-auto no-scrollbar scroll-smooth pl-0 pr-4 sm:pr-6 lg:pr-8 pb-1"
                    style={{ WebkitOverflowScrolling: "touch" }}
                >
                    {tiles.map((t) => (
                        <CarouselTile key={t.id} tile={t} />
                    ))}
                </div>
            </div>
        </section>
    );
}

const slugifyParam = (s = "") => encodeURIComponent(s); // kept for legacy fallback path

function CarouselTile({ tile }) {
    const dept = tile.department_slug || "clothing";
    // Route to the *category* landing page (/c/<name>) which lists every item
    // in that category across the catalogue. Falls back to the department
    // landing only when this tile somehow has no category (legacy data).
    const href = tile.category_name
        ? `/c/${encodeURIComponent(tile.category_name)}`
        : `/d/${dept}`;
    const initial = (tile.title || tile.category_name || "?").trim().charAt(0).toUpperCase();
    return (
        <Link
            to={href}
            data-testid={`carousel-tile-${tile.id}`}
            className="flex-shrink-0 flex flex-col items-center gap-1.5 lg:gap-2.5 group/tile"
            style={{ width: CATEGORY_TILE_WIDTH }}
            aria-label={tile.title}
        >
            {/* Elegant double-ring frame:
                  · outer rosegold halo (group-hover deepens to wine)
                  · ivory gap-ring for a "matted print" look
                  · soft drop shadow + inset highlight on the photo */}
            <div
                className="relative rounded-full p-[3px] bg-gradient-to-br from-rosegold/70 via-rosegold/40 to-wine/50 shadow-[0_6px_16px_-8px_rgba(120,75,55,0.35)] group-hover/tile:from-rosegold group-hover/tile:via-wine/70 group-hover/tile:to-wine group-hover/tile:shadow-[0_10px_22px_-10px_rgba(120,75,55,0.55)] transition-all duration-300"
                style={{ width: CATEGORY_TILE_SIZE, height: CATEGORY_TILE_SIZE }}
            >
                <div className="w-full h-full rounded-full p-[3px] bg-ivory">
                    <div className="relative w-full h-full rounded-full overflow-hidden bg-ivory-soft">
                        {tile.image_url ? (
                            <ResponsiveImg
                                src={tile.image_url}
                                alt={`${tile.title || tile.category_name || "Boutique"} category`}
                                loading="lazy"
                                sizes="8rem"
                                decoding="async"
                                className="w-full h-full object-cover object-center transition-transform duration-500 group-hover/tile:scale-110"
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-ivory-soft to-rosegold/15 font-heading text-2xl text-rosegold">
                                {initial}
                            </div>
                        )}
                        {/* Inset gloss — subtle highlight on top half of the
                            photo gives the tile a 3D, premium feel without
                            being shiny/AI-slop. */}
                        <span className="pointer-events-none absolute inset-0 rounded-full ring-1 ring-inset ring-white/30" />
                        <span className="pointer-events-none absolute inset-0 rounded-full bg-gradient-to-b from-white/15 via-transparent to-transparent" />
                    </div>
                </div>
            </div>
            <span className="text-[11px] lg:text-[12.5px] font-semibold text-ink text-center leading-tight max-w-[100%] line-clamp-1 group-hover/tile:text-rosegold transition-colors">
                {tile.title}
            </span>
        </Link>
    );
}
